import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ReceiptRefundIcon } from '@heroicons/react/24/outline';

import ModalDelete from '../../../components/modal-delete/modal-delete';
import Modal from '../../../components/modal/modal';
import Notification from '../../../components/notification/notification';
import orderApi from '../../../services/orderApi';
import refundApprovalApi from '../../../services/refundApprovalApi';
import { UserRoles } from '../../../data/enums';

import SlideOver from './slide-overs';
import Rating from './components/Rating';
import StatusActionsBar from './components/status-actions-bar';
import StoreHeader from './components/store-header';
import PickupTimeBanner from './components/pickup-time-banner';
import SummaryCards from './components/summary-cards';
import ItemsList from './components/items-list';
import TotalsSummary from './components/totals-summary';
import PaymentCard from './components/payment-card';
import RefundApprovalCard from './components/refund-approval-card';
import LogsTabs from './components/logs-tabs';
import TaxFeesDialog from './components/tax-fees-dialog';
import RefundDialog from './components/refund-dialog';
import CustomerEditDialog from './components/customer-edit-dialog';
import SendEmailDialog from './components/send-email-dialog';
import OrderDetailSkeleton from './components/order-detail-skeleton';
import { PageHeader, TWButton } from '../../../components/ui';

import './order-detail.css';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.Ls.en.weekStart = 1;

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userRoles = useSelector((state) => state.authReducer.roles);
  const isAdmin = userRoles.includes(UserRoles.ADMIN);

  const [orderDetail, setOrderDetail] = useState({});
  const [selectedExtraTime, setSelectedExtraTime] = useState(null);
  const [showSlide, setShowSlide] = useState(false);
  const [isSendSms, setIsSendSms] = useState(true);
  const [approveLoading, setApproveLoading] = useState(false);

  const [form, setForm] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
    refund: '',
  });
  const [sendEmailForm, setSendEmailForm] = useState({ email: '' });

  const [loading, setLoading] = useState({
    initialLoading: true,
    upsertLoading: false,
    deleteLoading: false,
    smsLoading: false,
    emailLoading: false,
  });

  const [errors, setErrors] = useState({
    code: false,
    name: false,
    refund: false,
  });

  const [dialogs, setDialogs] = useState({
    upsert: false,
    delete: false,
    refund: false,
    taxandFees: false,
    sendEmailConfirm: false,
    sendSmsConfirm: false,
  });

  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const onFormChange = (val, key) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const getOrderDetail = async () => {
    try {
      setLoading((prev) => ({ ...prev, initialLoading: true }));
      const res = await orderApi.getById(id);
      if (res.data?.data) setOrderDetail(res.data.data);
    } catch (error) {
      // swallow
    } finally {
      setLoading((prev) => ({ ...prev, initialLoading: false }));
    }
  };

  const onFormSubmit = async () => {
    if (!form.name) {
      setErrors((prev) => ({ ...prev, name: true }));
      return;
    }
    setErrors((prev) => ({ ...prev, name: false }));
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const res = await orderApi.upsert({ ...form });
      if (res.data.data) setDialogs((prev) => ({ ...prev, upsert: false }));
    } catch (error) {
      // swallow
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getOrderDetail();
    }
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({ ...prev, code: false, name: false }));
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickDelete = () => setDialogs((prev) => ({ ...prev, delete: true }));

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await orderApi.deleteById(orderDetail.id);
    } catch (error) {
      // swallow
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      navigate(-1);
    }
  };

  const onClickSendEmail = async (email) => {
    try {
      setLoading((prev) => ({ ...prev, emailLoading: true }));
      const res = await orderApi.sendInvoiceByEmail(
        orderDetail.id,
        sendEmailForm.email ||
          orderDetail.email ||
          orderDetail.customer?.email ||
          email,
      );
      if (res.data) {
        setNotf({
          display: true,
          type: 'success',
          message: 'E-mail successfully sent',
        });
      }
    } catch (error) {
      // swallow
    } finally {
      setLoading((prev) => ({ ...prev, emailLoading: false }));
      setDialogs((prev) => ({ ...prev, sendEmailConfirm: false }));
    }
  };

  const onClickSendSms = async () => {
    try {
      setLoading((prev) => ({ ...prev, smsLoading: true }));
      const res = await orderApi.sendInvoiceBySms(
        orderDetail.id,
        orderDetail.phone,
      );
      if (res.data) {
        setNotf({
          display: true,
          type: 'success',
          message: 'SMS successfully sent',
        });
      }
    } catch (error) {
      // swallow
    } finally {
      setLoading((prev) => ({ ...prev, smsLoading: false }));
      setDialogs((prev) => ({ ...prev, sendSmsConfirm: false }));
    }
  };

  const onClickOpenReceiptNewTab = () => {
    window.open(
      `${process.env.REACT_APP_BASE_URL}/receipt/${orderDetail.id}`,
      '_blank',
    );
  };

  const onClickOpenStripeNewTab = () => {
    window.open(
      `${process.env.REACT_APP_BASE_STRIPE_PAYMENT_URL + orderDetail.paymentid}`,
      '_blank',
    );
  };

  const onChangeOrderStatus = async (orderstatus, acceptplus) => {
    try {
      await orderApi.changeOrderStatus({
        orderid: orderDetail.id,
        orderstatus,
        acceptplus: acceptplus || 0,
        smsbypass: isSendSms,
      });
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setSelectedExtraTime(null);
      getOrderDetail();
    }
  };

  const onClickRefund = () => {
    const remaining = (orderDetail.total || 0) - (orderDetail.refund || 0);
    setForm({
      refund: Math.max(remaining, 0).toFixed(2),
      orderstatus: 40,
      orderid: orderDetail.id,
    });
    setDialogs((prev) => ({ ...prev, refund: true }));
  };

  const onFormRefundSubmit = async () => {
    const remaining = (orderDetail.total || 0) - (orderDetail.refund || 0);
    const invalid =
      !form.refund ||
      Number(form.refund) > remaining ||
      Number(form.refund) < 0;
    if (invalid) {
      setErrors((prev) => ({ ...prev, refund: true }));
      return;
    }
    setErrors((prev) => ({ ...prev, refund: false }));
    try {
      setLoading((prev) => ({ ...prev, initialLoading: true }));
      await orderApi.changeOrderStatus({ ...form });
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, initialLoading: false }));
      setDialogs((prev) => ({ ...prev, refund: false }));
      getOrderDetail();
    }
  };

  const onCloseRefundUpsert = () => {
    setErrors((prev) => ({ ...prev, refund: false }));
    setDialogs((prev) => ({ ...prev, refund: false }));
  };

  const onApproveRefund = async () => {
    const approvalId = orderDetail?.refundapproval?.id;
    if (!approvalId) return;
    try {
      setApproveLoading(true);
      await refundApprovalApi.approve(approvalId);
      setNotf({
        display: true,
        type: 'success',
        message: 'Refund approved successfully',
      });
      getOrderDetail();
    } catch (error) {
      setNotf({
        display: true,
        type: 'error',
        message: error?.response?.data || 'Failed to approve refund',
      });
    } finally {
      setApproveLoading(false);
    }
  };

  const onClickOpenEmailModal = () => {
    setSendEmailForm({
      email: orderDetail.email || orderDetail.customer?.email || '',
    });
    setDialogs((prev) => ({ ...prev, sendEmailConfirm: true }));
  };

  const onClickOpenSmsModal = () => {
    setDialogs((prev) => ({ ...prev, sendSmsConfirm: true }));
  };

  useEffect(() => {
    getOrderDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading.initialLoading) {
    return <OrderDetailSkeleton />;
  }

  if (!orderDetail || !orderDetail.store) {
    return <OrderDetailSkeleton />;
  }

  const remainingBalance =
    Number(orderDetail.total || 0) - Number(orderDetail.refund || 0);
  const canShowRefundButton =
    ![30].includes(orderDetail.orderstatus) &&
    remainingBalance > 0.009 &&
    !orderDetail.hasrefundapproval;

  const showApprovalCard =
    isAdmin &&
    orderDetail.hasrefundapproval &&
    orderDetail.refundapproval &&
    orderDetail.refundapproval.status === 0 &&
    remainingBalance > 0.009 &&
    Number(orderDetail.refundapproval.amount || 0) > 0;

  return (
    <div className="order-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Order Details"
          description="Review the receipt, customer info, and take action on this order."
        />

        <StatusActionsBar
          orderStatus={orderDetail.orderstatus}
          selectedExtraTime={selectedExtraTime}
          setSelectedExtraTime={setSelectedExtraTime}
          isSendSms={isSendSms}
          setIsSendSms={setIsSendSms}
          onChangeOrderStatus={onChangeOrderStatus}
        />

        <StoreHeader
          store={orderDetail.store}
          onClickEmail={onClickOpenEmailModal}
          onClickSms={onClickOpenSmsModal}
          onClickReceipt={onClickOpenReceiptNewTab}
        />

        <PickupTimeBanner
          pickuptime={orderDetail.pickuptime}
          store={orderDetail.store}
          timezone={orderDetail.timezone}
        />

        <SummaryCards orderDetail={orderDetail} />

        {orderDetail.orderitems?.length > 0 && (
          <ItemsList
            items={orderDetail.orderitems}
            itemfee={orderDetail.itemfee}
          />
        )}

        <TotalsSummary
          orderDetail={orderDetail}
          onClickTaxFees={() =>
            setDialogs((prev) => ({ ...prev, taxandFees: true }))
          }
        />

        <PaymentCard orderDetail={orderDetail} />

        <Rating order={orderDetail} color={orderDetail.store?.sitecolor} />

        {showApprovalCard && (
          <RefundApprovalCard
            approval={orderDetail.refundapproval}
            approveLoading={approveLoading}
            onApprove={onApproveRefund}
          />
        )}

        {canShowRefundButton && (
          <div className="flex justify-end">
            <TWButton
              icon={ReceiptRefundIcon}
              label="Make a Refund"
              onClick={onClickRefund}
              className="!border-rose-200 !text-rose-700 hover:!bg-rose-50 dark:!border-rose-900/50 dark:!text-rose-300 dark:hover:!bg-rose-900/20"
            />
          </div>
        )}

        {isAdmin && (
          <LogsTabs
            orderDetail={orderDetail}
            onClickOpenStripeNewTab={onClickOpenStripeNewTab}
          />
        )}
      </div>

      <TaxFeesDialog
        open={dialogs.taxandFees}
        onClose={() => setDialogs((prev) => ({ ...prev, taxandFees: false }))}
        orderDetail={orderDetail}
      />

      <RefundDialog
        open={dialogs.refund}
        onClose={onCloseRefundUpsert}
        form={form}
        errors={errors}
        loading={loading.initialLoading}
        onChange={onFormChange}
        onSubmit={onFormRefundSubmit}
        orderDetail={orderDetail}
      />

      <CustomerEditDialog
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        form={form}
        errors={errors}
        loading={loading}
        onChange={onFormChange}
        onSubmit={onFormSubmit}
        onDelete={onClickDelete}
      />

      <SendEmailDialog
        open={dialogs.sendEmailConfirm}
        onClose={() => {
          setDialogs((prev) => ({ ...prev, sendEmailConfirm: false }));
          setSendEmailForm({ email: '' });
        }}
        email={sendEmailForm.email}
        setEmail={(email) => setSendEmailForm({ email })}
        loading={loading.emailLoading}
        onConfirm={() => onClickSendEmail(sendEmailForm.email)}
      />

      <Modal
        title="Send SMS"
        text={`Are you sure you want to send an SMS to ${
          orderDetail.phone || orderDetail.customer?.phone
        }?`}
        loading={loading.smsLoading}
        open={dialogs.sendSmsConfirm}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, sendSmsConfirm: false }))
        }
        item={{ name: orderDetail?.phone, id: orderDetail?.id }}
        onOk={onClickSendSms}
      />

      <ModalDelete
        title="Delete Order"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={orderDetail}
      />

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />

      <SlideOver
        orderDetail={orderDetail}
        showSlide={showSlide}
        setShowSlide={setShowSlide}
        onClickSendEmail={onClickSendEmail}
        onClickSendSms={onClickSendSms}
        onClickOpenReceiptNewTab={onClickOpenReceiptNewTab}
        onClickOpenStripeNewTab={onClickOpenStripeNewTab}
      />
    </div>
  );
};

export default OrderDetail;
